<template>
  <q-page class="q-pa-md">
    <div class="row justify-center">
      <div class="col-md-6 col-12">
        <q-card class="change-password-card">
          <q-card-section>
            <div class="text-h5 text-center q-mb-md">Change Password</div>

            <q-form class="q-gutter-md" @submit.prevent="changePassword">
              <q-input
                ref="currentPasswordRef"
                filled
                v-model="currentPassword"
                label="Current Password"
                type="password"
                autocomplete="current-password"
                :rules="[(v) => !!v || 'Current password is required']"
              />

              <q-input
                filled
                v-model="newPassword"
                label="New Password"
                :type="showNewPwd ? 'text' : 'password'"
                autocomplete="new-password"
                :rules="passwordRules"
              >
                <template #append>
                  <q-icon
                    :name="showNewPwd ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="showNewPwd = !showNewPwd"
                  />
                </template>
              </q-input>

              <q-input
                filled
                v-model="confirmPassword"
                label="Confirm New Password"
                :type="showConfirmPwd ? 'text' : 'password'"
                autocomplete="new-password"
                :rules="confirmPasswordRules"
              >
                <template #append>
                  <q-icon
                    :name="showConfirmPwd ? 'visibility_off' : 'visibility'"
                    class="cursor-pointer"
                    @click="showConfirmPwd = !showConfirmPwd"
                  />
                </template>
              </q-input>

              <q-btn
                label="Change Password"
                color="primary"
                unelevated
                size="lg"
                class="full-width"
                :loading="loading"
                type="submit"
              />

              <div v-if="error" class="text-negative q-mt-md">{{ error }}</div>
              <div v-if="success" class="text-positive q-mt-md">
                {{ success }}
              </div>
            </q-form>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-page>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { api } from '@/api';

const router = useRouter();

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showNewPwd = ref(false);
const showConfirmPwd = ref(false);

const loading = ref(false);
const error = ref('');
const success = ref('');

const currentPasswordRef = ref(null);

// Password validation rules
const passwordRules = [
  (v) => !!v || 'New password is required',
  (v) => v.length >= 6 || 'Password must be at least 6 characters',
  (v) =>
    /[!@#$%^&*()_+\-=[\]{};':"|,.<>?]/.test(v) ||
    'Password must contain at least one special character',
  (v) =>
    v !== currentPassword.value ||
    'New password must be different from current password',
];

const confirmPasswordRules = [
  (v) => !!v || 'Please confirm your new password',
  (v) => v === newPassword.value || 'Passwords do not match',
];

async function changePassword() {
  if (!navigator.onLine) {
    error.value =
      'You are offline. Please connect to the internet and try again.';
    return;
  }

  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    await api.post('iam/change-password/', {
      current_password: currentPassword.value,
      new_password: newPassword.value,
    });

    success.value = 'Password changed successfully!';

    // Clear form
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';

    // Redirect after a delay
    setTimeout(() => {
      router.push('/iam/users');
    }, 2000);
  } catch (e) {
    if (e.response?.status === 400) {
      error.value = e.response.data?.error || 'Invalid current password';
    } else {
      error.value = 'Failed to change password. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.change-password-card {
  max-width: 500px;
  margin: 0 auto;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .change-password-card {
    margin: 16px;
    max-width: none;
  }

  .q-card-section {
    padding: 16px !important;
  }

  .q-form .q-gutter-md > * {
    margin-bottom: 16px !important;
  }
}
</style>
